import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Radio, FileText, Activity, ArrowUpRight, AlertTriangle } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "../lib/api";
import { OverviewSkeleton } from "../components/PageSkeletons";
import NotificationBell from "../components/NotificationBell";

const kpi = (icon, label, value, sub) => ({ icon, label, value, sub });

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [alertData, setAlertData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/sync/status").then(({ data }) => setLogs(data.logs)).catch(() => {});
    api.get("/alerts").then(({ data }) => setAlertData(data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="space-y-8 fade-up" data-testid="overview-loading">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading font-light text-3xl tracking-tight">Overview</h1>
            <p className="text-sm text-neutral-500 mt-1">Live quality picture across your service floor.</p>
          </div>
          <NotificationBell />
        </div>
        <OverviewSkeleton />
      </div>
    );
  }

  const cards = [
    kpi(Activity, "Average QoS score", stats.avg_score ?? "—", `${stats.total_sessions} sessions evaluated`),
    kpi(Users, "Employees", stats.total_employees, "across all departments"),
    kpi(Radio, "Active pendants", stats.active_devices, "paired & reporting"),
    kpi(FileText, "SOPs synced", `${stats.sops_synced}/${stats.sops_total}`, "to local server LS-01"),
  ];

  return (
    <div className="space-y-8 fade-up">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading font-light text-3xl tracking-tight" data-testid="overview-title">Overview</h1>
          <p className="text-sm text-neutral-500 mt-1">Live quality picture across your service floor.</p>
        </div>
        <NotificationBell />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={c.label} data-testid={`kpi-card-${i}`} className={`rounded-lg border p-6 ${i === 0 ? "bg-[#111] text-white border-[#111]" : "bg-white border-neutral-200"}`}>
            <c.icon className={`w-4 h-4 ${i === 0 ? "text-neutral-400" : "text-neutral-500"}`} />
            <div className="font-heading text-3xl font-light mt-4">{c.value}</div>
            <div className={`text-xs mt-1 ${i === 0 ? "text-neutral-400" : "text-neutral-500"}`}>{c.label}</div>
            <div className={`text-[11px] mt-2 font-mono-data ${i === 0 ? "text-neutral-500" : "text-neutral-400"}`}>{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg" data-testid="alerts-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${alertData?.alerts?.length ? "text-red-600" : "text-neutral-400"}`} />
            <h2 className="text-sm font-medium">Score alerts</h2>
            {alertData?.alerts?.length > 0 && (
              <span data-testid="alerts-count" className="text-[11px] font-mono-data bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5">
                {alertData.alerts.length}
              </span>
            )}
          </div>
          <span data-testid="alert-threshold-display" className="text-xs text-neutral-500 font-mono-data">
            Alert below {alertData?.threshold ?? "—"}
          </span>
        </div>
        {alertData?.alerts?.length > 0 ? (
          <ul className="divide-y divide-neutral-100 max-h-[260px] overflow-y-auto" data-testid="alerts-list">
            {alertData.alerts.map((a, i) => (
              <li key={`${a.employee_id}-${a.date}-${i}`} data-testid={`alert-item-${i}`} className="px-6 py-3 flex items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0" />
                  <Link to={`/console/employees/${a.employee_id}`} className="font-medium hover:underline underline-offset-4 truncate">{a.employee_name}</Link>
                  <span className="text-xs text-neutral-400 hidden sm:inline">{a.department}</span>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="text-[11px] text-neutral-400 font-mono-data">{a.date}</span>
                  <span className="font-heading text-red-600">{a.score}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-6 py-5 text-sm text-neutral-400" data-testid="no-alerts">
            No sessions below {alertData?.threshold ?? "—"} in the last 7 days. Your floor is holding the standard.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-lg p-6" data-testid="score-trend-chart">
          <h2 className="text-sm font-medium">Score trend — last 30 days</h2>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.trend}>
                <defs>
                  <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#111111" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#111111" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eeeeee" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#999" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis domain={[50, 100]} tick={{ fontSize: 10, fill: "#999" }} width={30} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e5e5" }} />
                <Area type="monotone" dataKey="score" stroke="#111111" strokeWidth={1.5} fill="url(#scoreFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="department-chart">
          <h2 className="text-sm font-medium">By department</h2>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.by_department} layout="vertical" margin={{ left: 8 }}>
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "#999" }} />
                <YAxis type="category" dataKey="department" tick={{ fontSize: 10, fill: "#555" }} width={78} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e5e5" }} />
                <Bar dataKey="score" fill="#111111" radius={[0, 4, 4, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg" data-testid="sync-activity">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h2 className="text-sm font-medium">Local server activity</h2>
          <Link to="/console/devices" className="text-xs text-neutral-500 hover:text-[#111] transition-colors inline-flex items-center gap-1">
            Manage devices <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
        <ul className="divide-y divide-neutral-100">
          {logs.slice(0, 6).map((log) => (
            <li key={log.id} className="px-6 py-3.5 flex items-start gap-3 text-sm">
              <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${log.status === "failed" ? "bg-red-600" : "bg-green-600"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-neutral-700">{log.message}</p>
                <p className="text-[11px] text-neutral-400 font-mono-data mt-0.5">{new Date(log.created_at).toLocaleString()}</p>
              </div>
            </li>
          ))}
          {logs.length === 0 && <li className="px-6 py-6 text-sm text-neutral-400">No sync activity yet.</li>}
        </ul>
      </div>
    </div>
  );
}
